import { useAtomValue, useSetAtom } from 'jotai';
import { Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import { userConfigsAtom } from '@/atoms/queries/userConfigs';
import { userConfigActionAtom, userConfigIdAtom } from '@/atoms/userConfigs';
import { useDuplicateUserSavedFilters } from '@/hooks/userFilters/useDuplicateUserSavedFilters';
import { UserConfigActions } from '@/types/userConfigs';
import { MenuItemWithIcon } from '../MenuItemWithIcon';
import { MoreOptionsMenu, MoreOptionsMenuFallback } from '../MoreOptionsMenu/MoreOptionsMenu';

const getConfigName = (configs: any[], configId: string, defaultValue = '') => {
  const userConfig = configs.find(config => config.configId === configId);
  return (userConfig?.definition as any)?.titleSettings?.title || defaultValue;
};

const RenameUserConfigMenuItem = ({ configId, onClick }: { configId: string; onClick: (name: string) => void }) => {
  const { t } = useTranslation('general');
  const userConfigs = useAtomValue(userConfigsAtom);

  return (
    <MenuItemWithIcon
      onClick={() => {
        const userConfigName = getConfigName(userConfigs.data, configId);
        onClick(userConfigName);
      }}
      icon="Edit"
      text={t('rename')}
    />
  );
};

const DeleteUserConfigMenuItem = ({
  configId,
  onClick,
}: {
  configId: string;
  onClick: (id: string, name: string) => void;
}) => {
  const { t } = useTranslation('general');
  const userConfigs = useAtomValue(userConfigsAtom);

  return (
    <MenuItemWithIcon
      onClick={() => {
        const userConfigNameToDelete = getConfigName(userConfigs.data, configId, t('untitled'));
        onClick(configId, userConfigNameToDelete);
      }}
      icon="Delete"
      text={t('delete')}
    />
  );
};

const DuplicateUserConfigMenuItem = ({ configId, onClick }: { configId: string; onClick: () => void }) => {
  const { t } = useTranslation('general');
  const userConfigs = useAtomValue(userConfigsAtom);

  const selectedFilterSetName = getConfigName(userConfigs.data, configId);
  const saveAsCopy = useDuplicateUserSavedFilters(selectedFilterSetName);

  return (
    <MenuItemWithIcon
      onClick={() => {
        saveAsCopy();
        onClick();
      }}
      icon="Clone"
      text={t('duplicate')}
    />
  );
};

export interface UserConfigMenuProps {
  configId: string;
  onRenameClick: (name: string) => void;
  onDeleteClick: (id: string, name: string) => void;
}

export const UserConfigMenu = ({ configId, onRenameClick, onDeleteClick }: UserConfigMenuProps) => {
  const { t } = useTranslation('userConfig');
  const setUserConfigIdToAmend = useSetAtom(userConfigIdAtom);
  const setUserConfigActions = useSetAtom(userConfigActionAtom);

  return (
    <Suspense fallback={<MoreOptionsMenuFallback />}>
      <MoreOptionsMenu title={t('menuTitle')} testId="user-config-menu">
        {/* suspense here as the options use async atoms */}
        <Suspense>
          <RenameUserConfigMenuItem
            configId={configId}
            onClick={(name: string) => {
              onRenameClick(name);

              setUserConfigIdToAmend(configId);
              setUserConfigActions(UserConfigActions.RENAME);
            }}
          />
          <DuplicateUserConfigMenuItem
            configId={configId}
            onClick={() => {
              setUserConfigIdToAmend(configId);
              setUserConfigActions(UserConfigActions.DUPLICATE);
            }}
          />
          <DeleteUserConfigMenuItem configId={configId} onClick={onDeleteClick} />
        </Suspense>
      </MoreOptionsMenu>
    </Suspense>
  );
};
